import { DatePipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, OnDestroy, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { dsEnums, webDtos } from '@vectronic/openApi-admin';
import { SchedulePosition, SchedulePreview, utcMoment } from '@vectronic/util';
import { combineLatest, Subscription } from 'rxjs';
import { AbstractScheduleService } from '../../services/abstract-schedule.service';
import { RemoteCommandsService } from '../../services/commands/remote-commands.service';
import { ScheduleCalendarCalculationService } from '../../services/schedule-calendar-calculation.service';
import { ScheduleServices } from '../../services/schedule.service';

@Component({
  selector: 'rc-calendar-month-view',
  templateUrl: './calendar-month-view.component.html',
  styleUrls: ['./calendar-month-view.component.scss'],
  imports: [MatIconButton, MatTooltip, MatIcon, NgTemplateOutlet, NgClass, NgStyle, DatePipe],
})
export class CalendarMonthViewComponent implements OnDestroy {
  private scheduleServices = inject(ScheduleServices);
  private remoteCommandsService = inject(RemoteCommandsService);
  private calendarCalculationService = inject(ScheduleCalendarCalculationService);

  selectedDate = input<Date | null>();
  scheduleType = input<dsEnums.ScheduleType>();

  rulesArray = signal<webDtos.ScheduleRule[]>(undefined);
  indexArray = signal<number[]>(undefined);
  daysPositions = signal<SchedulePosition[][]>(undefined);
  days = signal<number[]>(undefined);
  weekdays = signal<string[]>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
  dayOffset = signal<number[]>([]);

  monthChange = output<Date>();
  dayChange = output<Date>();
  back = output();

  subscription: Subscription;

  scheduleService = computed<AbstractScheduleService>(() => {
    if (!this.scheduleType()) return undefined;
    return this.scheduleServices[this.scheduleType()];
  });

  constructor() {
    toObservable(this.scheduleService)
      .pipe(takeUntilDestroyed())
      .subscribe((service) => {
        this.subscription?.unsubscribe();
        if (service) {
          this.subscription = combineLatest([
            service.rules$,
            service.fixPerTransmission$,
          ]).subscribe(([rules]) => {
            this.rulesArray.set(Object.values(rules));
            this.indexArray.set(Object.keys(rules).map((i) => parseInt(i)));
            this.updateDaysPositions();
          });
        }
      });

    toObservable(this.selectedDate)
      .pipe(takeUntilDestroyed())
      .subscribe((selectedDate) => {
        this.dayOffset.set(
          Array.from(
            { length: (utcMoment(selectedDate, true).startOf('month').day() + 6) % 7 },
            (_, i) => i + 1,
          ),
        );
        this.days.set(
          Array(utcMoment(selectedDate, true).daysInMonth())
            .fill(0)
            .map((x, i) => i + 1),
        );
        this.updateDaysPositions();
      });
  }

  onClickItem(index: number, isClickable: boolean) {
    if (isClickable)
      this.dayChange.emit(
        utcMoment(this.selectedDate(), true)
          .set('date', index + 1)
          .toDate(),
      );
  }

  onClickBack() {
    this.back.emit();
  }

  onClickPrevious() {
    this.monthChange.emit(utcMoment(this.selectedDate(), true).add('month', -1).toDate());
  }

  onClickNext() {
    this.monthChange.emit(utcMoment(this.selectedDate(), true).add('month', 1).toDate());
  }

  positionsInDay(day: number): SchedulePosition[] {
    const schedules: SchedulePreview[] = [];
    const service = this.scheduleService();
    if (service) {
      this.rulesArray()?.forEach((rule, i) => {
        const index = this.indexArray()?.[i];
        schedules.push(
          ...this.calendarCalculationService.schedulesInDay(
            rule,
            utcMoment(this.selectedDate(), true).set('date', day),
            index,
            service.config,
            service.fixPerTransmission$.value?.[index],
          ),
        );
      });
    }
    const r = 25;
    const offset = 2;
    return schedules.map((schedule) => {
      const time = utcMoment(schedule.millis, true);
      const hour = parseInt(time.format('H'));
      const minute = parseInt(time.format('m'));
      const partialHour = hour + minute / 60;
      return {
        bottom: `${r + r * Math.cos((partialHour * 3.14) / 12) - offset}px`,
        left: `${r + r * Math.sin((partialHour * 3.14) / 12) - offset}px`,
        isSkipped: schedule.isSkipped,
        isTransmitted: schedule.isTransmitted,
        label: time.format('ddd HH:mm'),
      };
    });
  }

  updateDaysPositions() {
    this.daysPositions.set(this.days()?.map((day) => this.positionsInDay(day)));
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  maxFix = computed(() => {
    return Math.max(...this.daysPositions().map((position) => position.length));
  });

  getPercent(average: number) {
    return Math.round((average * 100) / this.maxFix()) || 0;
  }

  getHourTooltip(label: string, isSkipped: boolean) {
    const supportsSkipCount = this.scheduleService()?.config?.hasSkipCount !== false;
    if (this.remoteCommandsService.hasNotShortHeaderFormat() && supportsSkipCount) {
      return (
        label +
        (isSkipped ? ' (Skipped)' : '') +
        `, The exact skipped position depends on the time when the collar is turned on and might not be correct in the preview!`
      );
    }
    return label + (isSkipped ? ' (Skipped)' : '');
  }
}
